import { Pica } from "pica"

import { READ_ERROR, SourceError } from "./SourceLoader.errors"
import { CursorGrid } from "../../shared/services"

import type { Crop } from "./FrameBuilder"
import type { Box } from "../types/Frame"
import type { Framing } from "../types/Framing"
import type { ResolvedResampling } from "../types/Resampling"

// Up to this alpha a pixel is empty to a trim, so a faint antialiased fringe still counts.
const EMPTY_ALPHA = 8

const pica = new Pica()

// Fit (contain) or fill (cover), centered on a transparent grid-sized canvas at intrinsic size,
// not devicePixelRatio. A crop draws only that part of the image, and may reach past its edges. The
// area is where the image sits, so it only means something for a fit of the whole image.
export const drawMaster = async (
  image: HTMLImageElement | ImageBitmap,
  width: number,
  height: number,
  type: string,
  framing: Framing,
  resampling: ResolvedResampling,
  crop?: Crop,
) => {
  const size = CursorGrid.SIZE
  const canvas = document.createElement("canvas")
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext("2d", { willReadFrequently: true })
  if (!context) throw new SourceError(READ_ERROR)

  const part = crop ?? { x: 0, y: 0, width, height }
  const scale =
    framing === "fit"
      ? Math.min(size / part.width, size / part.height)
      : Math.max(size / part.width, size / part.height)
  // Canvas smoothing aliases when it shrinks a bitmap. An SVG is exempt: it rasterizes at the drawn
  // size. pica has no nearest-neighbour filter, so a crisp draw stays on the canvas.
  if (scale < 1 && type !== "image/svg+xml" && resampling === "smooth") {
    const resized = document.createElement("canvas")
    resized.width = Math.max(1, Math.round(part.width * scale))
    resized.height = Math.max(1, Math.round(part.height * scale))
    if (crop) {
      const cropped = await createImageBitmap(image, crop.x, crop.y, crop.width, crop.height)
      try {
        await pica.resize(cropped, resized, { filter: "lanczos3" })
      } finally {
        cropped.close()
      }
    } else {
      await pica.resize(image, resized, { filter: "lanczos3" })
    }
    // Whole-pixel offsets, so the copy doesn't resample the result.
    const area = {
      x: Math.floor((size - resized.width) / 2),
      y: Math.floor((size - resized.height) / 2),
      width: resized.width,
      height: resized.height,
    }
    context.drawImage(resized, area.x, area.y)
    return { canvas, context, area }
  }

  const drawWidth = part.width * scale
  const drawHeight = part.height * scale
  const left = (size - drawWidth) / 2
  const top = (size - drawHeight) / 2
  context.imageSmoothingEnabled = resampling === "smooth"
  context.imageSmoothingQuality = "high"
  // A crop draws the whole image clipped to it: an SVG without a size of its own can't be cut from.
  if (crop) {
    // On whole pixels for a crisp draw: an antialiased clip edge would blend the art into the padding.
    const edge = resampling === "crisp" ? Math.round : (value: number) => value
    const x = edge(left)
    const y = edge(top)
    context.rect(x, y, edge(left + drawWidth) - x, edge(top + drawHeight) - y)
    context.clip()
  }
  context.drawImage(
    image,
    left - part.x * scale,
    top - part.y * scale,
    width * scale,
    height * scale,
  )
  // A pixel the image covers only partly is blended with the padding, so it is left out.
  const area = {
    x: Math.ceil(left),
    y: Math.ceil(top),
    width: Math.floor(left + drawWidth) - Math.ceil(left),
    height: Math.floor(top + drawHeight) - Math.ceil(top),
  }
  return { canvas, context, area }
}

// The smallest box of whole pixels that holds every visible one; null when none is.
export const findVisibleBox = ({ data, width, height }: ImageData): Box | null => {
  let left = width
  let top = height
  let right = 0
  let bottom = 0
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] <= EMPTY_ALPHA) continue

      left = Math.min(left, x)
      top = Math.min(top, y)
      right = Math.max(right, x + 1)
      bottom = y + 1
    }
  }
  return right > 0 ? { left, top, right, bottom } : null
}

// Whether a box found on the grid takes in every whole pixel of the image's box.
export const reachesEdges = (found: Box, image: Box) =>
  found.left <= Math.ceil(image.left) &&
  found.top <= Math.ceil(image.top) &&
  found.right >= Math.floor(image.right) &&
  found.bottom >= Math.floor(image.bottom)

// How drawMaster fits a crop on the grid: source pixel x lands on grid x * scale + x offset.
const fitOnGrid = (crop: Crop) => {
  const scale = CursorGrid.SIZE / Math.max(crop.width, crop.height)
  return {
    scale,
    x: (CursorGrid.SIZE - crop.width * scale) / 2 - crop.x * scale,
    y: (CursorGrid.SIZE - crop.height * scale) / 2 - crop.y * scale,
  }
}

// Where a crop sits on the grid the whole image is fitted on.
export const toGrid = (crop: Crop, image: Crop): Box => {
  const { scale, x, y } = fitOnGrid(image)
  return {
    left: crop.x * scale + x,
    top: crop.y * scale + y,
    right: (crop.x + crop.width) * scale + x,
    bottom: (crop.y + crop.height) * scale + y,
  }
}

// The source pixels under a box on the grid a crop is fitted on, kept within the crop.
export const toSource = (box: Box, crop: Crop): Crop => {
  const { scale, x, y } = fitOnGrid(crop)
  const left = Math.max(crop.x, Math.floor((box.left - x) / scale))
  const top = Math.max(crop.y, Math.floor((box.top - y) / scale))
  const right = Math.min(crop.x + crop.width, Math.ceil((box.right - x) / scale))
  const bottom = Math.min(crop.y + crop.height, Math.ceil((box.bottom - y) / scale))
  return { x: left, y: top, width: right - left, height: bottom - top }
}
